import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

type InviteInfo = {
  userName: string;
  email: string;
  expired: boolean;
};

async function errorMessageFromResponse(response: Response): Promise<string> {
  try {
    const data: unknown = await response.json();
    if (
      data &&
      typeof data === 'object' &&
      'message' in data &&
      typeof (data as { message?: unknown }).message === 'string'
    ) {
      return (data as { message: string }).message;
    }
  } catch {
    // ignore
  }

  return `Request failed (${response.status})`;
}

export function PortalInviteAcceptPage() {
  const { token } = useParams({ from: '/portal/invite/$token' });

  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [isLoadingInviteInfo, setIsLoadingInviteInfo] = useState(true);
  const [inviteInfoError, setInviteInfoError] = useState<string | null>(null);

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoadingInviteInfo(true);
      setInviteInfoError(null);

      const response = await fetch(
        `/portal/auth/invite-info?token=${encodeURIComponent(token)}`,
        { credentials: 'include' },
      );

      if (!response.ok) {
        const msg = await errorMessageFromResponse(response);
        if (!cancelled) setInviteInfoError(msg);
        if (!cancelled) setInviteInfo(null);
        if (!cancelled) setIsLoadingInviteInfo(false);
        return;
      }

      const data: InviteInfo = await response.json();
      if (!cancelled) setInviteInfo(data);
      if (!cancelled) setIsLoadingInviteInfo(false);
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    if (!password || !passwordConfirm) {
      setFormError('Please enter and confirm your password.');
      return;
    }

    if (password !== passwordConfirm) {
      setFormError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`/portal/auth/accept-invite`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });

      if (!response.ok) {
        setFormError(await errorMessageFromResponse(response));
        return;
      }

      const data: { email?: string; redirectUrl?: string } =
        await response.json();
      const email = typeof data.email === 'string' ? data.email : '';
      const redirectUrl =
        typeof data.redirectUrl === 'string'
          ? data.redirectUrl
          : '/portal/welcome';
      const search = email ? `?email=${encodeURIComponent(email)}` : '';
      window.location.href = `${redirectUrl}${search}`;
    } catch {
      setFormError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  const isExpired = inviteInfo?.expired ?? false;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Activate your portal account</CardTitle>
          <CardDescription>
            {isLoadingInviteInfo
              ? 'Loading invitation...'
              : inviteInfo
                ? `${inviteInfo.userName} • ${inviteInfo.email}`
                : 'Unable to load invitation'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {inviteInfoError && (
            <div className="text-sm text-destructive mb-4">
              {inviteInfoError}
            </div>
          )}

          {isExpired && (
            <div className="text-sm text-destructive mb-4">
              This invite link has expired.
            </div>
          )}

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-1.5">
              <Label htmlFor="portal-new-password">Password</Label>
              <Input
                id="portal-new-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={
                  isSubmitting ||
                  isLoadingInviteInfo ||
                  !!inviteInfoError ||
                  isExpired
                }
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="portal-new-password-confirm">
                Confirm password
              </Label>
              <Input
                id="portal-new-password-confirm"
                type="password"
                autoComplete="new-password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                disabled={
                  isSubmitting ||
                  isLoadingInviteInfo ||
                  !!inviteInfoError ||
                  isExpired
                }
              />
            </div>
            {formError && (
              <div className="text-sm text-destructive">{formError}</div>
            )}
            <Button
              className="w-full"
              type="submit"
              disabled={
                isSubmitting ||
                isLoadingInviteInfo ||
                !!inviteInfoError ||
                isExpired
              }
            >
              {isSubmitting ? 'Activating...' : 'Activate account'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
